import {
  animate,
  animateChild,
  group,
  query,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Sanitizer,
  SecurityContext,
  inject,
  viewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { NgIconComponent } from '@ng-icons/core';

import { MenuItem } from '@models/view/menu.model';

import { AnalyticsLabelValuePipe } from '@cxc/ui-kit/pipes';

import { RouterState } from '@cxc/core/services';
import { DashboardState } from '@services/state/dashboard.state';
import { OperationsState } from '@services/state/operation-state/operation.state';

@Component({
  selector: 'main-drawer',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgIconComponent,
    AnalyticsLabelValuePipe,
  ],
  templateUrl: './main-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .scrollbar-hide {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }

    .scrollbar-hide::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }
  `,
  animations: [
    trigger('expandColapseMainDrawer', [
      state('true', style({ minWidth: '264px' })),
      state('false', style({ minWidth: '66px' })),
      transition('true => false', [
        group([
          animate('0.25s ease-out'),
          query('@expandColapseVertical', animateChild()),
          query('@rotate90Clockwise', animateChild()),
        ]),
      ]),
      transition('false => true', [
        group([
          animate('0.25s ease-out'),
          query('@expandColapseVertical', animateChild({ delay: '0.2s' })),
          query('@rotate90Clockwise', animateChild({ delay: '0.2s' })),
        ]),
      ]),
    ]),

    trigger('expandColapseVertical', [
      state('true', style({ height: '*' })),
      state('false', style({ height: '0px' })),
      transition('* => *', [animate('0.25s ease-out')]),
    ]),

    trigger('rotate90Clockwise', [
      state('true', style({ transform: 'rotate(0)' })),
      state('false', style({ transform: 'rotate(90deg)' })),
      transition('* => *', [animate('0.25s ease-out')]),
    ]),
  ],
})
export class MainDrawerComponent {
  private navContainer = viewChild<ElementRef<HTMLElement>>('navContainer');

  dashboardState = inject(DashboardState);
  router = inject(Router);
  operationsState = inject(OperationsState);
  appState = inject(RouterState);
  sanitizer = inject(Sanitizer);

  SecurityContext = SecurityContext;
  private isDragging = false;
  private startY = 0;
  private scrollTop = 0;

  originalOrder = () => 0;

  isMenuOrSubitemActive(menu: MenuItem): boolean {
    const subitemsActive = menu.subitems?.some(sub =>
      this.appState.isRouteActive(sub.activePaths ?? [sub.path])
    );

    return subitemsActive || this.appState.isRouteActive([menu.path]);
  }

  clickAction(menu: MenuItem) {
    /**
     * Behaviour (22/05/2025):
     *
     * - If main drawer is collapsed, touching any
     * menu item will open the drawer
     *
     * - If drawer is open, touching the menu item
     * which corresponds to the active route, will
     * collapse the menu
     *
     * - If drawer is open, touching the menu item
     * which *does not* correspond to the active
     * route, will navifate to the route asociated
     * with the menu item
     */

    // If drawer is collapsed, open
    if (!this.dashboardState.mainDrawer()) {
      this.dashboardState.mainDrawer.toggle();
      return;
    }

    // Drawer is open
    // Is Active route == Menu item selected ? then toggle
    if (this.appState.isRouteActive([menu.path])) {
      this.dashboardState.mainDrawer.toggle();
      return;
    }

    // Active route != Menu item selected, then navigate to route
    this.router.navigateByUrl(menu.path);
  }

  onDragStart(event: MouseEvent): void {
    if (!this.navContainer()?.nativeElement) return;

    this.isDragging = true;

    // Record the initial mouse position and scroll position
    this.startY = event.pageY;
    this.scrollTop = this.navContainer()!.nativeElement.scrollTop;
    // Attach global listeners to handle dragging even if mouse leaves the element
    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.onDragEnd);
  }

  onDragMove = (event: MouseEvent): void => {
    if (!this.isDragging || !this.navContainer()?.nativeElement) return;
    event.preventDefault(); // Prevent default text selection behaviors
    const element = this.navContainer()!.nativeElement;
    // Calculate how far the mouse has moved
    const walkY = (event.pageY - this.startY) * 1.5; // Multiply by 1.5 for faster scrolling speed (adjust as needed)
    // Apply the new scroll position
    element.scrollTop = this.scrollTop - walkY;
  };

  onDragEnd = (): void => {
    if (!this.isDragging) return;
    this.isDragging = false;
    // Clean up global listeners
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
  };
}
